import { useEffect, useRef } from 'react';
import { useApp } from '../store/app';
import { openBook } from '../player/engine';
import { formatTime } from '../lib/format';
import { NmButton } from './nm';
import { CloudIcon } from './icons';

/**
  * Offered — never applied silently — when another device reports a
  * meaningfully different position for the book you have open. Jumping the
  * playhead without asking is the single most annoying thing a sync feature can
  * do, so this asks instead.
  */
export function ResumeDialog() {
   const prompt = useApp((s) => s.resumePrompt);
   const books = useApp((s) => s.books);
   const accept = useApp((s) => s.acceptResumePrompt);
   const dismiss = useApp((s) => s.dismissResumePrompt);
   const confirmRef = useRef<HTMLButtonElement>(null);

   useEffect(() => {
      if (prompt) confirmRef.current?.focus();
   }, [prompt]);

   useEffect(() => {
      if (!prompt) return;
      const onKey = (e: KeyboardEvent) => {
         if (e.key === 'Escape') dismiss();
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
   }, [prompt, dismiss]);

   if (!prompt) return null;

   const book = books.find((b) => b.id === prompt.bookId);

   const onConfirm = () => {
      accept();
      if (book) {
         void openBook(book, {
           trackIndex: prompt.trackIndex,
           positionSec: prompt.positionSec,
           autoplay: false,
         });
      }
   };

   return (
      <div className="scrim" role="presentation" onClick={dismiss}>
         <div
           className="nm-raised nm-lg dialog"
           role="alertdialog"
           aria-modal="true"
           aria-labelledby="resume-title"
           onClick={(e) => e.stopPropagation()}
         >
           <span className="dialog__icon nm-inset nm-sm">
              <CloudIcon size={22} />
           </span>
           <h2 id="resume-title" className="dialog__title">
              Resume from {prompt.device}?
           </h2>
           <p className="dialog__body">
              <strong>{prompt.bookTitle}</strong> was last played on {prompt.device} at{' '}
              <span className="tnum">{formatTime(prompt.positionSec)}</span>
              {prompt.trackIndex > 0 && ` in file ${prompt.trackIndex + 1}`}.
           </p>
           <div className="dialog__actions">
              <NmButton onClick={dismiss}>Stay here</NmButton>
              <NmButton ref={confirmRef} className="nmbtn--primary" onClick={onConfirm}>
                Jump there
              </NmButton>
           </div>
         </div>
      </div>
   );
}
